// Expanded Ask contract (focused-contract.md): /ask executes explicit one-copy
// edits and returns receipt + entry + revision, or a clarification
// { kind: 'entry_patch', patch, candidates } that the NEXT ask answers with the
// same conversation and previous_request_id. Manual finish saves go through
// the shared companion action route with the exact revision, then refetch.

// `index` (0-based) prefixes the label with its ordinal position so a spoken or
// typed "the second one" matches what is on screen: the candidate order is the
// backend's stored order and is never re-sorted here.
export function clarificationLabel(c, t = (k) => k, index = null) {
  if (!c) return '';
  const bits = [index != null ? String(index + 1) : null, c.name || t('companion.card'), c.printing, c.condition, c.location_name ? `${c.location_name}${c.page ? ` p.${c.page}` : ''}` : null, c.entry_id != null ? `#${c.entry_id}` : null].filter(Boolean);
  return bits.join(' · ');
}

// The message that answers a clarification by choosing one physical copy.
export function clarificationAnswer(c) { return c && c.entry_id != null ? `copy #${c.entry_id}` : ''; }

// Whether the inspector form asks for a different finish than the row shows.
export function finishChanged(card, printing) {
  return !!printing && printing !== (card?.printing || 'Normal');
}

// Route for a manual finish change, decided from the EXACT entry read back
// from the server, never from the grouped card prop: a Collection tile that
// says quantity 2 is usually two physical qty-1 rows, and the first of them is
// a perfectly good single-copy target. A row that is itself a stack is
// refused outright (no legacy fallback that would restamp the whole stack).
export function finishRoute(rawEntry, printing) {
  if (!printing) return { route: 'none' };
  const qty = Number(rawEntry?.quantity ?? 1);
  if (qty !== 1) return { route: 'refuse', reason: 'stacked_copy' };
  return { route: 'companion', patch: { printing }, expected_revision: rawEntry?.revision };
}

// Baseline of the inspector form as the reset effect seeds it from the card,
// expressed in the shape the entry update sends, so a finish-only save can be
// told apart from a save that also touched other fields.
export function formBaseline(card) {
  const c = card || {};
  return {
    condition: c.condition || 'Near Mint',
    variant_id: c.variant_id || null,
    variant_label: c.variant_label || null,
    foil_pattern: c.foil_pattern || null,
    distribution: c.distribution || null,
    language: c.language || 'English',
    purchase_price: parseFloat(c.purchase_price || 0) || 0,
    location_id: c.location_id ? parseInt(c.location_id, 10) : null,
    list_type: c.list_type || 'collection',
    is_trade: c.is_trade ? 1 : 0,
    favorite: c.favorite ? 1 : 0,
    notes: c.notes || '',
    grader: c.grader || 'Raw',
    grade: c.grade == null || c.grade === '' ? null : parseFloat(c.grade),
    cert_number: (c.cert_number || '').trim() || null,
  };
}

function same(a, b) {
  const n = (v) => (v === undefined || v === null || v === '' ? null : typeof v === 'boolean' ? (v ? 1 : 0) : typeof v === 'number' ? String(v) : String(v));
  return n(a) === n(b);
}

// True when the entry-update body changes anything besides the finish.
// quantity and market_value are only present in the body when edited.
export function otherFieldsChanged(card, body) {
  const base = formBaseline(card);
  for (const [k, v] of Object.entries(body || {})) {
    if (k === 'printing') continue;
    if (k === 'quantity') { if (Number(v) !== Number(card?.quantity ?? 1)) return true; continue; }
    if (k === 'market_value') { if (!same(v, card?.market_value)) return true; continue; }
    if (!(k in base)) { if (v !== undefined && v !== null && v !== '') return true; continue; }
    if (!same(v, base[k])) return true;
  }
  return false;
}

// Retry = the IDENTICAL request under the same request_id (the server replays a
// completed result or re-runs a failed one; it never writes twice). Only an
// errored turn with its request snapshot can be retried; cancelled turns are the
// user's choice and pending ones are still running.
export function retryRequest(turn) {
  if (!turn || turn.status !== 'error' || !turn.request) return null;
  return { ...turn.request, retry_of_request_id: turn.request.request_id };
}

const norm = (s) => String(s || '').toLowerCase().replace(/[^a-z0-9]+/g, ' ').trim();
const RETRY_PHRASES = /^(?:yes[ ,]*)?(?:please[ ,]*)?(?:try(?: that| it)? again|retry(?: that)?|do (?:that|it) again|say that again|once more|again)(?:[ ,]*please)?$/;
const scopeOf = (c = {}) => JSON.stringify(['entry_id', 'set_id', 'location_id', 'compartment_id', 'opening_id', 'game', 'page'].map(k => (c && c[k] != null ? String(c[k]) : null)));

// A spoken retry: Live re-delegates under a NEW delegation id. It is the retry
// of the latest turn only when that turn errored, was spoken, the words are a
// "try again" variant or the same question, the context is unchanged, and
// nothing newer has been asked since. A completed mutation is never retried.
export function matchesFailedVoiceTurn(thread, message, context = null) {
  const turns = (thread || []).filter(t => t.kind !== 'spoken');
  const last = turns[turns.length - 1];
  if (!last || last.status !== 'error' || last.input_mode !== 'voice' || !last.request) return null;
  if (context && scopeOf(last.request.context) !== scopeOf(context)) return null;
  const said = norm(message);
  return said === norm(last.request.message) || RETRY_PHRASES.test(said) ? last : null;
}

// Chips come from the turn's own clarification or from a pending clarification
// the backend carried across an unrelated turn; only the LATEST turn offers
// them (an earlier answered turn must not keep live choices), and the order is
// the backend's displayed order, never re-sorted.
export function chipsFor(turn, isLatest) {
  if (!turn || !isLatest || turn.receipt) return [];
  const src = turn.clarification?.kind === 'entry_patch' ? turn.clarification : turn.pending?.kind === 'entry_patch' ? turn.pending : null;
  return src && Array.isArray(src.candidates) ? src.candidates.slice(0, 6) : [];
}
